import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, X, Copy, Ban, RefreshCw, Loader2 } from "lucide-react";

// V1.54 · Auditoría de enlaces (72 h): fecha y hora exacta de creación,
// cronómetro de caducidad en vivo, estado y acciones cancelar / regenerar.
const KIND_BADGE = {
  cuestionario_horarios: "bg-green-100 text-green-800 border-green-300",
  cuestionario_kai: "bg-orange-100 text-orange-800 border-orange-300",
  firma_contrato: "bg-blue-100 text-blue-800 border-blue-300",
  firma_dgt: "bg-violet-100 text-violet-800 border-violet-300",
  alta_digital: "bg-cyan-100 text-cyan-800 border-cyan-300",
};

const STATUS_BADGE = {
  pending: "bg-emerald-50 text-emerald-800 border-emerald-300",
  sent: "bg-emerald-50 text-emerald-800 border-emerald-300",
  opened: "bg-sky-50 text-sky-800 border-sky-300",
  completed: "bg-neutral-100 text-neutral-600 border-neutral-300",
  signed: "bg-neutral-100 text-neutral-600 border-neutral-300",
  used: "bg-neutral-100 text-neutral-600 border-neutral-300",
  expired: "bg-amber-50 text-amber-800 border-amber-300",
  cancelled: "bg-red-50 text-red-800 border-red-300",
};

const ACTIVE = ["pending", "sent", "opened", "otp_sent", "otp_verified"];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch (_) { return iso; }
}

function Countdown({ expiresAt, status }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!ACTIVE.includes(status) || !expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (isNaN(ms)) return null;
  if (ms <= 0) return <span className="text-[10px] font-bold text-amber-700">CADUCADO</span>;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const urgent = ms < 3600000;
  return (
    <span className={"font-mono tabular-nums text-[11px] font-bold " + (urgent ? "text-red-600" : "text-neutral-600")}>
      caduca en {h > 0 ? `${h}h ` : ""}{String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s
    </span>
  );
}

export const SecureLinksOverlay = ({ open, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/secure-links", { params: { hours: 72 } });
      setRows(data || []);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const copy = (r) => {
    navigator.clipboard.writeText(`${window.location.origin}${r.url_path}`);
    toast.success("Enlace copiado");
  };

  const cancel = async (r) => {
    setBusyId(r.id);
    try {
      await api.post("/secure-links/cancel", { kind: r.kind, id: r.id });
      toast.success("Enlace cancelado: ya no es accesible");
      await load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  const regenerate = async (r) => {
    setBusyId(r.id);
    try {
      if (ACTIVE.includes(r.status)) {
        await api.post("/secure-links/cancel", { kind: r.kind, id: r.id }).catch(() => {});
      }
      let url = "";
      if (r.kind === "cuestionario_horarios") {
        const { data } = await api.post(`/students/${r.student_id}/availability-questionnaire`);
        url = data.portal_url;
      } else if (r.kind === "cuestionario_kai") {
        const { data } = await api.post("/kai-questionnaire-link", { student_id: r.student_id || "" });
        url = data.portal_url;
      }
      if (url) {
        navigator.clipboard.writeText(url);
        toast.success("Nuevo enlace único generado y copiado (el anterior queda cancelado)");
      }
      await load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-end bg-black/40 p-4" data-testid="secure-links-overlay" onClick={onClose}>
      <div
        className="w-full max-w-xl max-h-[85vh] flex flex-col rounded-2xl border border-neutral-200 bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 bg-neutral-900 text-white shrink-0">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          <span className="text-xs font-bold uppercase tracking-wider">Auditoría de enlaces · últimas 72 h</span>
          <button type="button" onClick={onClose} data-testid="secure-links-close" className="ml-auto text-neutral-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto divide-y divide-neutral-100">
          {loading && <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-neutral-400" /></div>}
          {!loading && rows.length === 0 && (
            <p className="p-6 text-center text-sm text-neutral-400 italic">Sin enlaces generados en las últimas 72 horas.</p>
          )}
          {rows.map((r) => (
            <div key={`${r.kind}-${r.id}`} className="px-4 py-2.5" data-testid={`secure-link-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${KIND_BADGE[r.kind] || ""}`}>{r.kind_label}</span>
                <span className="text-xs font-semibold text-neutral-800 truncate">{r.student_label || "— sin alumno —"}</span>
                <span className={`ml-auto inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[r.status] || "bg-neutral-100 text-neutral-600 border-neutral-300"}`}>
                  {r.status === "pending" ? "activo" : r.status}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-neutral-500">
                <span className="font-mono tabular-nums">Creado {fmtDateTime(r.created_at)}</span>
                <Countdown expiresAt={r.expires_at} status={r.status} />
                {r.status === "cancelled" && r.cancelled_at && (
                  <span className="font-mono tabular-nums text-red-600">cancelado {fmtDateTime(r.cancelled_at)}</span>
                )}
              </div>
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                {ACTIVE.includes(r.status) && (
                  <>
                    <button type="button" onClick={() => copy(r)} data-testid={`secure-link-copy-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 px-2 h-6 text-[10px] font-semibold">
                      <Copy className="h-3 w-3" /> Copiar
                    </button>
                    <button type="button" onClick={() => cancel(r)} disabled={busyId === r.id} data-testid={`secure-link-cancel-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 px-2 h-6 text-[10px] font-bold">
                      <Ban className="h-3 w-3" /> Cancelar
                    </button>
                  </>
                )}
                {["cuestionario_horarios", "cuestionario_kai"].includes(r.kind) && r.status !== "completed" && (
                  <button type="button" onClick={() => regenerate(r)} disabled={busyId === r.id || (r.kind === "cuestionario_horarios" && !r.student_id)}
                    data-testid={`secure-link-regen-${r.id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 px-2 h-6 text-[10px] font-bold disabled:opacity-40">
                    {busyId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Regenerar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="px-4 py-2 text-[10px] text-neutral-400 border-t border-neutral-100 shrink-0">
          Cada enlace es único y de un solo uso; nunca se repite. Cancelar lo invalida al instante en todos los dispositivos.
        </p>
      </div>
    </div>
  );
};

export default SecureLinksOverlay;
